import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Camera, Plus, Trash, Notepad, Users, ClipboardText,
  CalendarBlank, CaretLeft, CaretRight, Image as ImageIcon,
  Package, ArrowsLeftRight, Warning, CheckCircle, ArrowClockwise,
  ChartBar,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ModuleGuide, EmptyState } from '../components/GuidanceComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LABOUR_TYPES = ['Skilled', 'Unskilled', 'Supervisor'];
const LABOUR_SOURCES = ['In-house', 'Subcontractor', 'Temporary'];
const MOVEMENT_TYPES = ['Issue', 'Return', 'Transfer'];
const GATE_PASS_TYPES = ['Returnable', 'Non-Returnable'];
const GP_STATUSES = ['Issued', 'Returned', 'Pending'];

const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};

export default function ExecutionCenter() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const [activeTab, setActiveTab] = useState('dpr');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [project, setProject] = useState(null);
  const [trades, setTrades] = useState([]);
  const [boqItems, setBoqItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // DPR
  const [dprEntries, setDprEntries] = useState([]);
  const [dprForm, setDprForm] = useState({ trade: '', boq_item_id: '', quantity_completed: '', remarks: '' });
  const [dprPhotos, setDprPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  // DMR
  const [dmrEntries, setDmrEntries] = useState([]);
  const [dmrForm, setDmrForm] = useState({ trade: '', labour_type: 'Skilled', source: 'In-house', count: '', remarks: '' });
  const [dmrDeviation, setDmrDeviation] = useState(null);

  // Site Log
  const [siteLogs, setSiteLogs] = useState([]);
  const [logForm, setLogForm] = useState({ notes: '', client_instructions: '', issues: '' });

  // Daily Summary
  const [summary, setSummary] = useState(null);

  // Assets
  const [assetEntries, setAssetEntries] = useState([]);
  const [assetForm, setAssetForm] = useState({ item: '', quantity: '', movement_type: 'Issue', remarks: '' });

  // Gate Pass
  const [gatePasses, setGatePasses] = useState([]);
  const [gpForm, setGpForm] = useState({ pass_type: 'Non-Returnable', destination: '', item: '', quantity: '', date_out: '', expected_return_date: '', remarks: '' });

  // Scan
  const [scanning, setScanning] = useState(false);

  const fetchBase = useCallback(async () => {
    try {
      const [projRes, tradeRes, boqRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/trades`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/boq-line-items`, { withCredentials: true }),
      ]);
      setProject(projRes.data);
      setTrades(tradeRes.data);
      setBoqItems(boqRes.data);
    } catch { toast.error('Failed to load project data'); }
    finally { setLoading(false); }
  }, [projectId]);

  const fetchDateData = useCallback(async () => {
    try {
      const [dprRes, dmrRes, logRes, sumRes, devRes, assetRes, gpRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/dpr?date=${date}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/dmr?date=${date}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/site-log?date=${date}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/daily-summary?date=${date}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/dmr-deviation?date=${date}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/asset-movements?date=${date}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/gate-passes`, { withCredentials: true }),
      ]);
      setDprEntries(dprRes.data);
      setDmrEntries(dmrRes.data);
      setSiteLogs(logRes.data);
      setSummary(sumRes.data);
      setDmrDeviation(devRes.data);
      setAssetEntries(assetRes.data);
      setGatePasses(gpRes.data);
    } catch { /* silent */ }
  }, [projectId, date]);

  useEffect(() => { fetchBase(); }, [fetchBase]);
  useEffect(() => { fetchDateData(); }, [fetchDateData]);

  const shiftDate = (dir) => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + dir);
    setDate(d.toISOString().split('T')[0]);
  };

  // --- DPR Handlers ---
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('photo', file);
        const res = await axios.post(`${BACKEND_URL}/api/uploads/dpr-photos`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        urls.push(res.data.url);
      }
      setDprPhotos(prev => [...prev, ...urls]);
      toast.success(`${urls.length} photo(s) uploaded`);
    } catch { toast.error('Photo upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const selectedBoqItem = boqItems.find(i => i.id === dprForm.boq_item_id);

  const handleDprSubmit = async () => {
    if (!dprForm.boq_item_id) { toast.error('Select a BOQ line item'); return; }
    if (!dprForm.quantity_completed) { toast.error('Enter quantity completed'); return; }
    if (dprPhotos.length === 0) { toast.error('At least 1 photo is mandatory'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/dpr`, {
        date, trade: selectedBoqItem?.trade_name || dprForm.trade, trade_id: selectedBoqItem?.trade_id || '',
        boq_item_id: dprForm.boq_item_id, boq_item_description: selectedBoqItem?.description || '',
        boq_item_unit: selectedBoqItem?.unit || '', boq_item_total_qty: selectedBoqItem?.quantity || 0,
        quantity_completed: parseFloat(dprForm.quantity_completed), remarks: dprForm.remarks, photos: dprPhotos,
      }, { withCredentials: true });
      toast.success('DPR entry saved');
      setDprForm({ trade: '', boq_item_id: '', quantity_completed: '', remarks: '' });
      setDprPhotos([]);
      fetchDateData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  // --- DMR Handlers ---
  const handleDmrSubmit = async () => {
    if (!dmrForm.trade) { toast.error('Select a trade'); return; }
    if (!dmrForm.count) { toast.error('Enter manpower count'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/dmr`, {
        date, ...dmrForm, count: parseInt(dmrForm.count),
      }, { withCredentials: true });
      toast.success('DMR entry saved');
      setDmrForm({ trade: '', labour_type: 'Skilled', source: 'In-house', count: '', remarks: '' });
      fetchDateData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  // --- Site Log Handlers ---
  const handleLogSubmit = async () => {
    if (!logForm.notes && !logForm.client_instructions && !logForm.issues) { toast.error('Enter at least one field'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/site-log`, { date, ...logForm }, { withCredentials: true });
      toast.success('Site log saved');
      setLogForm({ notes: '', client_instructions: '', issues: '' });
      fetchDateData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  // --- Asset Movement Handlers ---
  const handleAssetSubmit = async () => {
    if (!assetForm.item) { toast.error('Enter item name'); return; }
    if (!assetForm.quantity) { toast.error('Enter quantity'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/asset-movements`, {
        ...assetForm, date, quantity: parseFloat(assetForm.quantity),
      }, { withCredentials: true });
      toast.success('Asset movement recorded');
      setAssetForm({ item: '', quantity: '', movement_type: 'Issue', remarks: '' });
      fetchDateData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  // --- Gate Pass Handlers ---
  const handleGpSubmit = async () => {
    if (!gpForm.item) { toast.error('Enter item details'); return; }
    if (!gpForm.destination) { toast.error('Enter destination'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/gate-passes`, {
        ...gpForm, date_out: gpForm.date_out || date, quantity: parseFloat(gpForm.quantity) || 0,
      }, { withCredentials: true });
      toast.success('Gate pass created');
      setGpForm({ pass_type: 'Non-Returnable', destination: '', item: '', quantity: '', date_out: '', expected_return_date: '', remarks: '' });
      fetchDateData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleGpStatusChange = async (id, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/gate-passes/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Status updated to ${status}`);
      fetchDateData();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/${type}/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchDateData();
    } catch { toast.error('Failed to delete'); }
  };

  // --- Execution Scan ---
  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/projects/${projectId}/execution-scan`, {}, { withCredentials: true });
      const d = res.data;
      if (d.tasks_created > 0) {
        toast.success(`Scan complete: ${d.tasks_created} task(s) created`);
      } else {
        toast.info('Scan complete — no deviations found');
      }
    } catch { toast.error('Scan failed'); }
    finally { setScanning(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="animate-pulse text-zinc-500">Loading...</div></div>;

  const tabs = [
    { key: 'dpr', label: 'DPR', icon: ClipboardText, count: dprEntries.length },
    { key: 'dmr', label: 'DMR', icon: Users, count: dmrEntries.length },
    { key: 'sitelog', label: 'Site Log', icon: Notepad, count: siteLogs.length },
    { key: 'assets', label: 'Assets', icon: Package, count: assetEntries.length },
    { key: 'gatepass', label: 'Gate Pass', icon: ArrowsLeftRight, count: gatePasses.length },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="execution-center">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <Button onClick={() => navigate(-1)} variant="ghost" className="rounded-none h-7 text-xs px-2" data-testid="back-button">
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
            <div className="flex items-center gap-2">
              <Button onClick={() => navigate(`/projects/${projectId}/execution/weekly-summary`)} variant="outline"
                className="rounded-none h-7 text-xs px-2 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="weekly-summary-btn">
                <ChartBar size={14} className="mr-1" /> Weekly Review
              </Button>
              {!isViewer && (
                <Button onClick={handleScan} disabled={scanning} variant="outline" className="rounded-none h-7 text-xs px-2 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="scan-button">
                  <ArrowClockwise size={14} className={`mr-1 ${scanning ? 'animate-spin' : ''}`} /> {scanning ? 'Scanning...' : 'Scan'}
                </Button>
              )}
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Execution Center</span>
            </div>
          </div>
          <h1 className="text-base font-bold text-zinc-900">{project?.pi_number}</h1>
          <p className="text-xs text-zinc-500">{project?.client_name}</p>

          {/* Date Picker */}
          <div className="flex items-center gap-2 mt-3 bg-zinc-50 border border-zinc-200 p-1.5">
            <button onClick={() => shiftDate(-1)} className="p-1 hover:bg-zinc-200" data-testid="date-prev"><CaretLeft size={16} /></button>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="flex-1 text-center text-sm font-medium bg-transparent border-none outline-none" data-testid="date-input" />
            <button onClick={() => shiftDate(1)} className="p-1 hover:bg-zinc-200" data-testid="date-next"><CaretRight size={16} /></button>
          </div>
          <p className="text-xs text-center text-zinc-500 mt-1">{fmtDate(date)}</p>

          {/* Summary Strip */}
          {summary && (
            <div className="grid grid-cols-3 gap-2 mt-3" data-testid="daily-summary">
              <div className="text-center bg-blue-50 border border-blue-200 py-1.5 px-2">
                <p className="text-lg font-bold text-blue-700">{summary.dpr_entries}</p>
                <p className="text-[9px] text-blue-600 font-semibold uppercase">Work Done</p>
              </div>
              <div className="text-center bg-green-50 border border-green-200 py-1.5 px-2">
                <p className="text-lg font-bold text-green-700">{summary.manpower_total}</p>
                <p className="text-[9px] text-green-600 font-semibold uppercase">Manpower</p>
              </div>
              <div className="text-center bg-amber-50 border border-amber-200 py-1.5 px-2">
                <p className="text-lg font-bold text-amber-700">{summary.site_logs}</p>
                <p className="text-[9px] text-amber-600 font-semibold uppercase">Site Logs</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-0 mt-3 border-b border-zinc-200 -mb-px overflow-x-auto">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`flex-1 min-w-0 flex items-center justify-center gap-1 py-2 text-[11px] font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === t.key ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
                }`}
                data-testid={`tab-${t.key}`}>
                <t.icon size={14} />
                {t.label}
                {t.count > 0 && <span className="bg-zinc-200 text-zinc-600 text-[9px] px-1 py-0.5 font-bold">{t.count}</span>}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* ===== DPR TAB ===== */}
        {activeTab === 'dpr' && (
          <div className="space-y-4">
            <ModuleGuide text="Record daily work progress. Select the BOQ item, enter quantity completed, and attach a site photo." />
            {!isViewer && (
              <div className="bg-white border border-zinc-200 p-4 space-y-3" data-testid="dpr-form">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">New Progress Entry</p>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-zinc-400">BOQ Line Item</Label>
                  <select value={dprForm.boq_item_id} onChange={(e) => setDprForm({ ...dprForm, boq_item_id: e.target.value })}
                    className="mt-1 w-full border border-zinc-300 p-2 text-sm bg-white" data-testid="dpr-boq-select">
                    <option value="">Select line item...</option>
                    {boqItems.map(i => <option key={i.id} value={i.id}>{i.trade_name ? `[${i.trade_name}] ` : ''}{i.description} ({i.quantity} {i.unit})</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Qty Completed</Label>
                    <Input type="number" step="0.01" value={dprForm.quantity_completed} onChange={(e) => setDprForm({ ...dprForm, quantity_completed: e.target.value })}
                      className="mt-1 rounded-none border-zinc-300 h-10 text-sm" placeholder="0" data-testid="dpr-qty-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Unit</Label>
                    <Input readOnly value={selectedBoqItem?.unit || ''} className="mt-1 rounded-none border-zinc-200 h-10 text-sm bg-zinc-50" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-zinc-400">Remarks</Label>
                  <Input value={dprForm.remarks} onChange={(e) => setDprForm({ ...dprForm, remarks: e.target.value })}
                    className="mt-1 rounded-none border-zinc-300 h-9 text-sm" placeholder="Optional notes" data-testid="dpr-remarks-input" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-zinc-400">Site Photos (Mandatory)</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {dprPhotos.map((url, idx) => (
                      <div key={idx} className="relative w-16 h-16 border border-zinc-200 bg-zinc-100">
                        <img src={`${BACKEND_URL}/api${url}`} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setDprPhotos(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute -top-1 -right-1 bg-red-500 text-white w-4 h-4 flex items-center justify-center text-[8px]">x</button>
                      </div>
                    ))}
                    <label className={`w-16 h-16 border-2 border-dashed border-zinc-300 flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-50 ${uploading ? 'opacity-50' : ''}`}>
                      <Camera size={18} className="text-zinc-400" />
                      <span className="text-[8px] text-zinc-400 mt-0.5">{uploading ? '...' : 'Add'}</span>
                      <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} disabled={uploading} data-testid="dpr-photo-input" />
                    </label>
                  </div>
                </div>
                <Button onClick={handleDprSubmit} className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none h-9 text-xs" data-testid="dpr-submit-btn">
                  <Plus size={14} className="mr-1" /> Save Progress Entry
                </Button>
              </div>
            )}
            {dprEntries.length === 0 ? (
              <EmptyState title="No progress entries for this date" description="Add your first entry above to record work completed today." testId="dpr-empty" />
            ) : (
              <div className="space-y-2">
                {dprEntries.map(e => (
                  <div key={e.id} className="bg-white border border-zinc-200 p-3" data-testid={`dpr-entry-${e.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-zinc-800">{e.boq_item_description}</p>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500">
                          {e.trade && <span className="bg-zinc-100 px-1.5 py-0.5 font-medium">{e.trade}</span>}
                          <span><strong>{e.quantity_completed}</strong> {e.boq_item_unit}</span>
                          <span className="font-mono text-zinc-400">{e.dpr_number}</span>
                        </div>
                        {e.remarks && <p className="text-[10px] text-zinc-500 mt-1">{e.remarks}</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        {e.photos?.length > 0 && <span className="flex items-center gap-0.5 text-[9px] text-zinc-400"><ImageIcon size={10} />{e.photos.length}</span>}
                        {!isViewer && <button onClick={() => handleDelete('dpr', e.id)} className="p-1 hover:bg-red-50 text-red-400"><Trash size={12} /></button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== DMR TAB ===== */}
        {activeTab === 'dmr' && (
          <div className="space-y-4">
            <ModuleGuide text="Record daily manpower on site. Select trade, type, and source. Deviations from plan are highlighted below." />

            {/* DMR Deviation Panel */}
            {dmrDeviation && dmrDeviation.deviations.length > 0 && (
              <div className="bg-white border border-zinc-200 p-3 space-y-2" data-testid="dmr-deviation-panel">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Planned vs Actual</p>
                  <div className="flex items-center gap-3 text-[10px]">
                    <span className="text-zinc-500">Planned: <strong>{dmrDeviation.total_planned}</strong></span>
                    <span className="text-zinc-500">Actual: <strong>{dmrDeviation.total_actual}</strong></span>
                    <span className={`font-bold ${dmrDeviation.total_deviation >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dmrDeviation.total_deviation > 0 ? '+' : ''}{dmrDeviation.total_deviation}
                    </span>
                  </div>
                </div>
                <div className="space-y-1">
                  {dmrDeviation.deviations.map((d, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-2 text-xs border ${
                      d.status === 'shortfall' ? 'bg-red-50 border-red-200' :
                      d.status === 'excess' ? 'bg-amber-50 border-amber-200' :
                      d.status === 'unplanned' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'
                    }`} data-testid={`deviation-row-${idx}`}>
                      <div className="flex-1">
                        <span className="font-semibold text-zinc-800">{d.trade_name}</span>
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          S:{d.actual_skilled}/{d.planned_skilled} U:{d.actual_unskilled}/{d.planned_unskilled} Sv:{d.actual_supervisor}/{d.planned_supervisor}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-500">P:{d.planned_total}</span>
                          <span className="text-zinc-800 font-bold">A:{d.actual_total}</span>
                          <span className={`font-bold ${d.deviation >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {d.deviation > 0 ? '+' : ''}{d.deviation}
                          </span>
                        </div>
                        <span className={`text-[9px] font-bold uppercase ${
                          d.status === 'shortfall' ? 'text-red-600' :
                          d.status === 'excess' ? 'text-amber-600' :
                          d.status === 'unplanned' ? 'text-blue-600' : 'text-green-600'
                        }`}>
                          {d.status === 'shortfall' && <Warning size={10} className="inline mr-0.5" />}
                          {d.status === 'on_track' && <CheckCircle size={10} className="inline mr-0.5" />}
                          {d.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Add */}
            {!isViewer && (
              <div className="bg-white border border-zinc-200 p-4 space-y-3" data-testid="dmr-form">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">New Manpower Entry</p>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-zinc-400">Trade</Label>
                  <select value={dmrForm.trade} onChange={(e) => setDmrForm({ ...dmrForm, trade: e.target.value })}
                    className="mt-1 w-full border border-zinc-300 p-2 text-sm bg-white" data-testid="dmr-trade-select">
                    <option value="">Select trade...</option>
                    {trades.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Type</Label>
                    <select value={dmrForm.labour_type} onChange={(e) => setDmrForm({ ...dmrForm, labour_type: e.target.value })}
                      className="mt-1 w-full border border-zinc-300 p-2.5 text-sm bg-white" data-testid="dmr-type-select">
                      {LABOUR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Source</Label>
                    <select value={dmrForm.source} onChange={(e) => setDmrForm({ ...dmrForm, source: e.target.value })}
                      className="mt-1 w-full border border-zinc-300 p-2.5 text-sm bg-white" data-testid="dmr-source-select">
                      {LABOUR_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Count</Label>
                    <Input type="number" value={dmrForm.count} onChange={(e) => setDmrForm({ ...dmrForm, count: e.target.value })}
                      className="mt-1 rounded-none border-zinc-300 h-10 text-sm" placeholder="0" data-testid="dmr-count-input" />
                  </div>
                </div>
                <Button onClick={handleDmrSubmit} className="w-full bg-green-700 hover:bg-green-800 text-white rounded-none h-10 text-xs" data-testid="dmr-submit-btn">
                  <Plus size={14} className="mr-1" /> Save Manpower Entry
                </Button>
              </div>
            )}

            {dmrEntries.length === 0 ? (
              <EmptyState title="No manpower entries for this date" description="Record the manpower present on site today." testId="dmr-empty" />
            ) : (
              <div className="space-y-2">
                {dmrEntries.map(e => (
                  <div key={e.id} className="bg-white border border-zinc-200 p-3 flex justify-between items-center" data-testid={`dmr-entry-${e.id}`}>
                    <div>
                      <p className="text-xs font-semibold text-zinc-800">{e.trade}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-zinc-500">
                        <span className="bg-zinc-100 px-1.5 py-0.5">{e.labour_type}</span>
                        <span className="bg-zinc-100 px-1.5 py-0.5">{e.source}</span>
                        <span className="font-mono text-zinc-400">{e.dmr_number}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-zinc-800">{e.count}</span>
                      {!isViewer && <button onClick={() => handleDelete('dmr', e.id)} className="p-1 hover:bg-red-50 text-red-400"><Trash size={12} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== SITE LOG TAB ===== */}
        {activeTab === 'sitelog' && (
          <div className="space-y-4">
            <ModuleGuide text="Quick daily notes — record updates, client instructions, and any blockers." />
            {!isViewer && (
              <div className="bg-white border border-zinc-200 p-4 space-y-3" data-testid="sitelog-form">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">New Site Log</p>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-zinc-400">Notes / Updates</Label>
                  <textarea value={logForm.notes} onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
                    className="mt-1 w-full border border-zinc-300 p-2.5 text-sm resize-none h-16" placeholder="What happened on site today..." data-testid="sitelog-notes" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-zinc-400">Client / Architect Instructions</Label>
                  <textarea value={logForm.client_instructions} onChange={(e) => setLogForm({ ...logForm, client_instructions: e.target.value })}
                    className="mt-1 w-full border border-zinc-300 p-2.5 text-sm resize-none h-14" placeholder="Instructions received..." data-testid="sitelog-instructions" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-zinc-400">Issues / Blockers</Label>
                  <textarea value={logForm.issues} onChange={(e) => setLogForm({ ...logForm, issues: e.target.value })}
                    className="mt-1 w-full border border-zinc-300 p-2.5 text-sm resize-none h-14" placeholder="Any issues or blockers..." data-testid="sitelog-issues" />
                </div>
                <Button onClick={handleLogSubmit} className="w-full bg-amber-700 hover:bg-amber-800 text-white rounded-none h-9 text-xs" data-testid="sitelog-submit-btn">
                  <Plus size={14} className="mr-1" /> Save Site Log
                </Button>
              </div>
            )}
            {siteLogs.length === 0 ? (
              <EmptyState title="No site logs for this date" description="Jot down today's site notes, client instructions, or any issues." testId="sitelog-empty" />
            ) : (
              <div className="space-y-2">
                {siteLogs.map(e => (
                  <div key={e.id} className="bg-white border border-zinc-200 p-3" data-testid={`sitelog-entry-${e.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1 space-y-1.5">
                        {e.notes && <div><p className="text-[9px] font-bold uppercase text-zinc-400">Notes</p><p className="text-xs text-zinc-700">{e.notes}</p></div>}
                        {e.client_instructions && <div><p className="text-[9px] font-bold uppercase text-blue-500">Client Instructions</p><p className="text-xs text-zinc-700">{e.client_instructions}</p></div>}
                        {e.issues && <div><p className="text-[9px] font-bold uppercase text-red-500">Issues</p><p className="text-xs text-zinc-700">{e.issues}</p></div>}
                        <p className="text-[9px] font-mono text-zinc-400">{e.log_number} — {e.created_by_name}</p>
                      </div>
                      {!isViewer && <button onClick={() => handleDelete('site-log', e.id)} className="p-1 hover:bg-red-50 text-red-400"><Trash size={12} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== ASSETS TAB ===== */}
        {activeTab === 'assets' && (
          <div className="space-y-4">
            <ModuleGuide text="Track material and asset movements — issues to site, returns to store, or transfers between locations." />
            {!isViewer && (
              <div className="bg-white border border-zinc-200 p-4 space-y-3" data-testid="asset-form">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">New Movement</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Item / Asset</Label>
                    <Input value={assetForm.item} onChange={(e) => setAssetForm({ ...assetForm, item: e.target.value })}
                      className="mt-1 rounded-none border-zinc-300 h-9 text-sm" placeholder="e.g. Cement Bags, Power Drill" data-testid="asset-item-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Quantity</Label>
                    <Input type="number" value={assetForm.quantity} onChange={(e) => setAssetForm({ ...assetForm, quantity: e.target.value })}
                      className="mt-1 rounded-none border-zinc-300 h-9 text-sm" placeholder="0" data-testid="asset-qty-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Movement Type</Label>
                    <select value={assetForm.movement_type} onChange={(e) => setAssetForm({ ...assetForm, movement_type: e.target.value })}
                      className="mt-1 w-full border border-zinc-300 p-2 text-sm bg-white" data-testid="asset-type-select">
                      {MOVEMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-zinc-400">Remarks</Label>
                  <Input value={assetForm.remarks} onChange={(e) => setAssetForm({ ...assetForm, remarks: e.target.value })}
                    className="mt-1 rounded-none border-zinc-300 h-9 text-sm" placeholder="Optional notes" data-testid="asset-remarks-input" />
                </div>
                <Button onClick={handleAssetSubmit} className="w-full bg-violet-700 hover:bg-violet-800 text-white rounded-none h-9 text-xs" data-testid="asset-submit-btn">
                  <Plus size={14} className="mr-1" /> Record Movement
                </Button>
              </div>
            )}
            {assetEntries.length === 0 ? (
              <EmptyState title="No asset movements for this date" description="Record material issues, returns, or transfers." testId="asset-empty" />
            ) : (
              <div className="space-y-2">
                {assetEntries.map(e => (
                  <div key={e.id} className="bg-white border border-zinc-200 p-3 flex justify-between items-center" data-testid={`asset-entry-${e.id}`}>
                    <div>
                      <p className="text-xs font-semibold text-zinc-800">{e.item}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-zinc-500">
                        <span className={`px-1.5 py-0.5 font-medium ${
                          e.movement_type === 'Issue' ? 'bg-blue-50 text-blue-700' :
                          e.movement_type === 'Return' ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'
                        }`}>{e.movement_type}</span>
                        <span>Qty: <strong>{e.quantity}</strong></span>
                        <span className="font-mono text-zinc-400">{e.am_number}</span>
                      </div>
                      {e.remarks && <p className="text-[10px] text-zinc-500 mt-1">{e.remarks}</p>}
                    </div>
                    {!isViewer && <button onClick={() => handleDelete('asset-movements', e.id)} className="p-1 hover:bg-red-50 text-red-400"><Trash size={12} /></button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== GATE PASS TAB ===== */}
        {activeTab === 'gatepass' && (
          <div className="space-y-4">
            <ModuleGuide text="Issue gate passes for materials leaving the site. Track returnable items and their status." />
            {!isViewer && (
              <div className="bg-white border border-zinc-200 p-4 space-y-3" data-testid="gatepass-form">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">New Gate Pass</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Type</Label>
                    <select value={gpForm.pass_type} onChange={(e) => setGpForm({ ...gpForm, pass_type: e.target.value })}
                      className="mt-1 w-full border border-zinc-300 p-2 text-sm bg-white" data-testid="gp-type-select">
                      {GATE_PASS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Destination</Label>
                    <Input value={gpForm.destination} onChange={(e) => setGpForm({ ...gpForm, destination: e.target.value })}
                      className="mt-1 rounded-none border-zinc-300 h-9 text-sm" placeholder="Vendor / Site / Factory" data-testid="gp-destination-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Item Details</Label>
                    <Input value={gpForm.item} onChange={(e) => setGpForm({ ...gpForm, item: e.target.value })}
                      className="mt-1 rounded-none border-zinc-300 h-9 text-sm" placeholder="Item description" data-testid="gp-item-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-zinc-400">Quantity</Label>
                    <Input type="number" value={gpForm.quantity} onChange={(e) => setGpForm({ ...gpForm, quantity: e.target.value })}
                      className="mt-1 rounded-none border-zinc-300 h-9 text-sm" placeholder="0" data-testid="gp-qty-input" />
                  </div>
                </div>
                {gpForm.pass_type === 'Returnable' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] font-bold uppercase text-zinc-400">Date Out</Label>
                      <Input type="date" value={gpForm.date_out || date} onChange={(e) => setGpForm({ ...gpForm, date_out: e.target.value })}
                        className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="gp-date-out" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase text-zinc-400">Expected Return</Label>
                      <Input type="date" value={gpForm.expected_return_date} onChange={(e) => setGpForm({ ...gpForm, expected_return_date: e.target.value })}
                        className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="gp-return-date" />
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-[10px] font-bold uppercase text-zinc-400">Remarks</Label>
                  <Input value={gpForm.remarks} onChange={(e) => setGpForm({ ...gpForm, remarks: e.target.value })}
                    className="mt-1 rounded-none border-zinc-300 h-9 text-sm" placeholder="Optional notes" data-testid="gp-remarks-input" />
                </div>
                <Button onClick={handleGpSubmit} className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-none h-9 text-xs" data-testid="gp-submit-btn">
                  <Plus size={14} className="mr-1" /> Issue Gate Pass
                </Button>
              </div>
            )}
            {gatePasses.length === 0 ? (
              <EmptyState title="No gate passes" description="Issue a gate pass when materials leave the site." testId="gatepass-empty" />
            ) : (
              <div className="space-y-2">
                {gatePasses.map(e => (
                  <div key={e.id} className="bg-white border border-zinc-200 p-3" data-testid={`gp-entry-${e.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-zinc-800">{e.gp_number}</span>
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${
                            e.pass_type === 'Returnable' ? 'bg-blue-50 text-blue-700' : 'bg-zinc-100 text-zinc-600'
                          }`}>{e.pass_type}</span>
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${
                            e.status === 'Issued' ? 'bg-amber-50 text-amber-700' :
                            e.status === 'Returned' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>{e.status}</span>
                        </div>
                        <p className="text-xs text-zinc-700 mt-1">{e.item} — Qty: {e.quantity}</p>
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          To: {e.destination} · Out: {fmtDate(e.date_out)}
                          {e.expected_return_date && ` · Return by: ${fmtDate(e.expected_return_date)}`}
                        </div>
                        {e.remarks && <p className="text-[10px] text-zinc-400 mt-0.5">{e.remarks}</p>}
                      </div>
                      <div className="flex flex-col gap-1 ml-2">
                        {!isViewer && e.status === 'Issued' && e.pass_type === 'Returnable' && (
                          <button onClick={() => handleGpStatusChange(e.id, 'Returned')}
                            className="text-[9px] bg-green-50 border border-green-200 text-green-700 px-2 py-1 font-semibold hover:bg-green-100"
                            data-testid={`gp-return-${e.id}`}>
                            Mark Returned
                          </button>
                        )}
                        {!isViewer && <button onClick={() => handleDelete('gate-passes', e.id)} className="p-1 hover:bg-red-50 text-red-400 self-end"><Trash size={12} /></button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
